import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '../../components/feature/SEOHead';

const servicesSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'True North Vacations – Full-Service Waterfront Property Management',
  provider: {
    '@type': 'LocalBusiness',
    name: 'True North Vacations',
    address: { '@type': 'PostalAddress', addressLocality: 'Port Severn', addressRegion: 'ON', addressCountry: 'CA' },
  },
  serviceType: 'Vacation Rental Property Management',
  areaServed: { '@type': 'Place', name: 'Port Severn, Ontario, Canada' },
  description: 'Comprehensive waterfront property management including maintenance, professional cleaning, multi-platform listing, 24/7 guest services, property protection, and financial management.',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Property Management Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Property Maintenance' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Professional Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Marketing & Listing' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Guest Services' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Property Protection' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Financial Management' } },
    ],
  },
};

export default function ServicesPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Waterfront Property Management Services | True North Vacations"
        description="True North Vacations offers full-service waterfront property management in Port Severn, Ontario — maintenance, professional cleaning, Airbnb & VRBO listing, 24/7 guest services, and financial reporting. Let us handle everything."
        keywords="waterfront property management services Ontario, cottage maintenance Port Severn, Airbnb VRBO listing management, vacation rental cleaning services, property management Port Severn"
        canonical={`${import.meta.env.VITE_SITE_URL || ''}/services`}
        ogTitle="Waterfront Property Management Services | True North Vacations"
        ogDescription="Full-service waterfront property management in Port Severn — maintenance, cleaning, listing, guest services and more."
        schemaJson={servicesSchema}
      />
      {/* Navigation */}
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-compass-3-line text-2xl text-[#0A2540]"></i>
              <span className="text-base sm:text-xl font-serif font-semibold text-[#0A2540]">True North Vacations</span>
            </Link>
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <Link to="/listings" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Properties</Link>
              <Link to="/why-list" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">For Owners</Link>
              <Link to="/services" className="text-sm font-light text-[#C9A961] whitespace-nowrap">Services</Link>
              <Link to="/owners-portal" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Owner Portal</Link>
              <Link to="/contact" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/contact" className="bg-[#C9A961] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors whitespace-nowrap">List Your Property</Link>
            </div>
            <button className="lg:hidden p-2 text-[#0A2540] cursor-pointer" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
          {menuOpen && (
            <div className="lg:hidden pt-4 pb-4 border-t border-gray-100 mt-4 space-y-3">
              <Link to="/listings" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Properties</Link>
              <Link to="/why-list" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">For Owners</Link>
              <Link to="/services" onClick={() => setMenuOpen(false)} className="block text-sm text-[#C9A961] py-2">Services</Link>
              <Link to="/owners-portal" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Owner Portal</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Contact</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="inline-block bg-[#C9A961] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors mt-2">List Your Property</Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[280px] sm:h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/bc0aa79c6ef5dc41fcdd0b1737b902b5.jpg" alt="Our Services" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-5xl font-serif text-white mb-3 sm:mb-4">Complete Property Care</h1>
          <p className="text-base sm:text-xl text-white/90 font-light">Comprehensive management services for waterfront properties</p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">Full-Service Management</h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto">From the moment you partner with True North Vacations, we take care of every aspect of your property management.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            {[
              { icon: 'ri-home-gear-line', title: 'Property Maintenance', description: 'Year-round exterior and interior maintenance including lawn care, snow removal, dock maintenance, and seasonal opening/closing.', features: ['Lawn & Landscaping', 'Snow Removal', 'Dock Maintenance', 'Seasonal Services', 'Emergency Repairs'] },
              { icon: 'ri-sparkling-2-line', title: 'Professional Cleaning', description: 'Deep cleaning before every guest arrival and departure, ensuring 5-star standards are maintained consistently.', features: ['Pre-Arrival Deep Clean', 'Post-Departure Inspection', 'Linen Service', 'Supply Restocking', 'Quality Assurance'] },
              { icon: 'ri-global-line', title: 'Marketing & Listing', description: 'Professional photography, compelling descriptions, and strategic listing across all major booking platforms.', features: ['Professional Photography', 'Multi-Platform Listing', 'SEO Optimization', 'Dynamic Pricing', 'Calendar Management'] },
              { icon: 'ri-customer-service-2-line', title: 'Guest Services', description: '24/7 guest communication, check-in coordination, and on-site support to ensure exceptional guest experiences.', features: ['24/7 Communication', 'Check-In/Check-Out', 'Guest Screening', 'Issue Resolution', 'Review Management'] },
              { icon: 'ri-shield-check-line', title: 'Property Protection', description: 'Comprehensive insurance coverage, regular inspections, and security measures to protect your investment.', features: ['Insurance Coverage', 'Regular Inspections', 'Security Monitoring', 'Damage Assessment', 'Claims Management'] },
              { icon: 'ri-file-text-line', title: 'Financial Management', description: 'Transparent accounting, monthly statements, tax documentation, and direct deposit payments.', features: ['Monthly Statements', 'Tax Documentation', 'Direct Deposit', 'Expense Tracking', 'Revenue Reporting'] }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-[#C9A961]/10 rounded-full flex items-center justify-center mb-5">
                  <i className={`${service.icon} text-3xl text-[#C9A961]`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#0A2540] mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-5 leading-relaxed text-sm sm:text-base">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700 text-sm">
                      <i className="ri-check-line text-[#C9A961] mr-2"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">Our Service Process</h2>
            <p className="text-gray-600 text-base sm:text-lg">How we ensure excellence at every step</p>
          </div>
          <div className="space-y-10 sm:space-y-12">
            {[
              { step: '01', title: 'Property Onboarding', description: 'We conduct a thorough property assessment, create a detailed inventory, and develop a customized management plan tailored to your property\'s unique features and your goals.', image: 'https://readdy.ai/api/search-image?query=professional%20property%20inspector%20with%20clipboard%20evaluating%20luxury%20waterfront%20cottage%20detailed%20assessment%20process%20quality%20inspection%20simple%20clean%20background&width=600&height=400&seq=process1&orientation=landscape' },
              { step: '02', title: 'Professional Setup', description: 'Our team photographs your property, creates compelling listings, sets up your owner portal, and launches your property across all major booking platforms with optimized pricing.', image: 'https://readdy.ai/api/search-image?query=professional%20photographer%20taking%20photos%20of%20beautiful%20waterfront%20property%20interior%20real%20estate%20photography%20equipment%20high%20quality%20listing%20images%20simple%20clean%20background&width=600&height=400&seq=process2&orientation=landscape' },
              { step: '03', title: 'Active Management', description: 'We handle all guest inquiries, bookings, communication, and coordination. Our team manages cleaning schedules, maintenance requests, and ensures your property is always guest-ready.', image: 'https://readdy.ai/api/search-image?query=property%20management%20team%20coordinating%20services%20on%20tablet%20professional%20communication%20and%20scheduling%20organized%20workflow%20simple%20clean%20background&width=600&height=400&seq=process3&orientation=landscape' },
              { step: '04', title: 'Ongoing Optimization', description: 'We continuously monitor performance, adjust pricing strategies, respond to market trends, and implement improvements to maximize your revenue and property value.', image: 'https://readdy.ai/api/search-image?query=business%20analytics%20dashboard%20showing%20property%20performance%20metrics%20revenue%20charts%20and%20graphs%20data%20optimization%20professional%20analysis%20simple%20clean%20background&width=600&height=400&seq=process4&orientation=landscape' }
            ].map((item, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="text-5xl font-bold text-[#C9A961]/20 mb-3">{item.step}</div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-[#0A2540] mb-4">{item.title}</h3>
                  <p className="text-gray-600 text-base sm:text-lg leading-relaxed">{item.description}</p>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img src={item.image} alt={item.title} className="w-full h-56 sm:h-80 object-cover rounded-2xl shadow-lg" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-12 sm:py-20 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">Technology-Powered Service</h2>
            <p className="text-gray-600 text-base sm:text-lg">Industry-leading tools for superior results</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            {[
              { icon: 'ri-calendar-line', title: 'Smart Scheduling', description: 'Automated booking management and calendar synchronization across all platforms' },
              { icon: 'ri-price-tag-3-line', title: 'Dynamic Pricing', description: 'AI-powered pricing optimization based on demand, seasonality, and local events' },
              { icon: 'ri-smartphone-line', title: 'Mobile Access', description: 'Manage your property from anywhere with our mobile-friendly owner portal' },
              { icon: 'ri-notification-3-line', title: 'Real-Time Alerts', description: 'Instant notifications for bookings, maintenance issues, and important updates' },
              { icon: 'ri-bar-chart-line', title: 'Performance Analytics', description: 'Detailed insights into occupancy rates, revenue trends, and market positioning' },
              { icon: 'ri-lock-2-line', title: 'Secure Platform', description: 'Bank-level security protecting your data and financial information' }
            ].map((tech, index) => (
              <div key={index} className="bg-white rounded-xl p-5 sm:p-8 text-center hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-[#0A2540] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${tech.icon} text-2xl sm:text-3xl text-[#C9A961]`}></i>
                </div>
                <h3 className="text-base sm:text-xl font-bold text-[#0A2540] mb-2 sm:mb-3">{tech.title}</h3>
                <p className="text-gray-600 text-sm">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[#0A2540]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Experience the Difference</h2>
          <p className="text-base sm:text-xl text-gray-300 mb-8 leading-relaxed">Let us show you how professional property management can transform your waterfront investment.</p>
          <Link to="/contact" className="inline-flex items-center space-x-2 bg-[#C9A961] text-white px-8 py-4 rounded-full text-lg hover:bg-[#B8984E] transition-all whitespace-nowrap">
            <span>Schedule a Consultation</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2540] text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-12">
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-compass-3-line text-2xl text-[#C9A961]"></i>
                <span className="text-lg font-serif font-semibold">True North Vacations</span>
              </div>
              <p className="text-gray-400 text-sm">Port Severn's Premier Luxury Vacation Rental Management</p>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services" className="hover:text-white transition-colors">Property Management</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Maintenance</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Guest Services</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">For Owners</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/contact" className="hover:text-white transition-colors">List Your Property</Link></li>
                <li><Link to="/owners-portal" className="hover:text-white transition-colors">Owner Portal</Link></li>
                <li><Link to="/why-list" className="hover:text-white transition-colors">Revenue Calculator</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Properties</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/listings" className="hover:text-white transition-colors">Current Listings</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Booking Inquiry</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2025 True North Vacations. All rights reserved.</p>
            <div className="flex items-center space-x-5">
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-facebook-fill text-xl"></i></a>
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-instagram-line text-xl"></i></a>
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-linkedin-fill text-xl"></i></a>
              <a href="mailto:info@truenorthvacations.com" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-mail-line text-xl"></i></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
